import { useCallback } from 'react';

import { useToast } from '@rbilabs/universal-components';
import { isEqual } from 'lodash-es';
import { useIntl } from 'react-intl';

import { ICartEntry, ICombo } from '@rbi-ctg/menu';
import type { IAddToCartSelectionAttributes } from 'state/amplitude/types';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { CartEntryType } from 'utils/cart';

interface IUseCart {
  cartEntries: ICartEntry[];
  addItemToCart: (
    cartEntry: ICartEntry,
    selectionAttrs?: IAddToCartSelectionAttributes | undefined,
    showMessage?: boolean
  ) => void;
  isCartEntryInSwaps: (cartEntry: ICartEntry) => boolean;
  removeAllFromCart: (cartEntries: ICartEntry[]) => void;
  removeFromCart: (objectWithCartId: { cartId: string }) => void;
  repriceCartEntries: (
    entries: ICartEntry[],
    parentEntry?: ICartEntry,
    mainCombo?: ICombo
  ) => ICartEntry[];
  updateQuantity: (cartId: string, quantity: number) => void;
}

/**
 * Expose the cart state and the utility methods to mutate it.
 *
 * When an action to mutate the cart is triggered, it manages the Redux cart actions, logs the events,
 * coordinates with other parts of the code, and dispatches actions on related slices.
 */
export const useCart = (): IUseCart => {
  const dispatch = useAppDispatch();
  const cartEntries = useAppSelector(selectors.ordering.selectCartEntries);
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);

  const toast = useToast();
  const { formatMessage } = useIntl();

  const { logAddOrRemoveFromCart, logEvent } = useMParticleContext();
  const {
    pricingFunction,
    priceForItemInComboSlotSelection,
    priceForItemOptionModifier,
  } = useMenuContext();

  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();

  // Adds a single cartEntry to the cart
  const addItemToCart = useCallback<IUseCart['addItemToCart']>(
    (cartEntry, selectionAttrs, showMessage = true) => {
      logAddOrRemoveFromCart({
        action: 'add',
        cartEntry,
        previousCartEntries: cartEntries,
        selectionAttrs,
      });

      dispatch(actions.ordering.addCartEntry(cartEntry));

      if (cartEntry.isDonation) {
        return;
      }

      if (showMessage) {
        toast.show({
          text: formatMessage({ id: 'addToCartSuccess' }, { itemName: cartEntry.name }),
          variant: 'positive',
        });
      }
    },
    [cartEntries, dispatch, formatMessage, logAddOrRemoveFromCart, toast]
  );

  const isCartEntryInSwaps = useCallback(
    (cartEntry: ICartEntry) =>
      appliedOffers.some(offer =>
        cartEntry?.children?.length
          ? offer.cartId === cartEntry.cartId
          : offer?.swap?.cartId === cartEntry.cartId
      ),
    [appliedOffers]
  );

  const logCartEntryRemovedFromCart = useCallback(
    (cartEntry: ICartEntry) => {
      logAddOrRemoveFromCart({
        action: 'remove',
        cartEntry,
        previousCartEntries: cartEntries,
      });
      if (cartEntry.isUpsell) {
        logEvent(CustomEventNames.UPSELL_REMOVED, EventTypes.Other, {
          Id: cartEntry._id,
          Name: cartEntry.name,
          Price: cartEntry.price && cartEntry.price / 100,
        });
      }
    },
    [cartEntries, logEvent, logAddOrRemoveFromCart]
  );

  const removeRewardIfNeeded = useCallback(
    (cartEntry: ICartEntry) => {
      const { cartId } = cartEntry;
      const cartEntryReward = getAvailableRewardFromCartEntry(cartEntry);

      const isRewardApplied = !!appliedLoyaltyRewards?.[cartId]?.timesApplied;
      if (loyaltyEnabled && isRewardApplied && cartEntryReward) {
        dispatch(
          actions.loyalty.removeAppliedReward({
            rewardBenefitId: cartEntryReward.rewardBenefitId,
            cartId,
          })
        );
      }
    },
    [appliedLoyaltyRewards, getAvailableRewardFromCartEntry, loyaltyEnabled, dispatch]
  );

  // Removes a single cartEntry using its cartId
  const removeFromCart = useCallback<IUseCart['removeFromCart']>(
    ({ cartId }: { cartId: string }) => {
      const cartEntryToRemove = cartEntries.find((entry: ICartEntry) => entry.cartId === cartId);

      if (!cartEntryToRemove) {
        return;
      }
      dispatch(actions.ordering.removeCartEntries(new Set([cartId])));

      // removes applied rewards associated to cart entry on removal
      removeRewardIfNeeded(cartEntryToRemove);
      dispatch(actions.loyalty.removeAppliedOfferByCartEntry(cartEntryToRemove));
      logCartEntryRemovedFromCart(cartEntryToRemove);

      // TODO: Analyze this further. I don't understand why we are clearing out Surprise when deleting a single cart entry
      // Resets the availability of Surprise so it wont show in cart page
      dispatch(actions.loyalty.resetSurpriseAvailability());
    },
    [cartEntries, dispatch, logCartEntryRemovedFromCart, removeRewardIfNeeded]
  );

  // Removes all provided cartEntries from the cart using their cartIds
  const removeAllFromCart = useCallback<IUseCart['removeAllFromCart']>(
    (cartEntriesToRemove = []) => {
      const cartEntryIdsToRemove = new Set(
        cartEntriesToRemove.map((entry: ICartEntry) => entry.cartId)
      );
      dispatch(actions.ordering.removeCartEntries(cartEntryIdsToRemove));

      cartEntriesToRemove.forEach(cartEntry => {
        removeRewardIfNeeded(cartEntry);
        dispatch(actions.loyalty.removeAppliedOfferByCartEntry(cartEntry));
        logCartEntryRemovedFromCart(cartEntry);
      });
    },
    [dispatch, logCartEntryRemovedFromCart, removeRewardIfNeeded]
  );

  const updateQuantity = useCallback<IUseCart['updateQuantity']>(
    (cartId: string, quantity: number) => {
      const cartEntryToUpdate = cartEntries.find(cartEntry => cartEntry.cartId === cartId);
      if (!cartEntryToUpdate) {
        return;
      }

      if (quantity < 1) {
        return removeFromCart({ cartId });
      }
      const price = pricingFunction({ item: cartEntryToUpdate, quantity });
      dispatch(actions.ordering.updateQuantityAndPrice({ cartId, quantity, price }));
    },
    [cartEntries, dispatch, pricingFunction, removeFromCart]
  );

  // When repricing cart entry, we need a record of parent and main combo in order to price combo item
  const repriceCartEntriesHelper = useCallback(
    (
      entries: ICartEntry[],
      parentEntry?: ICartEntry,
      mainCombo?: ICartEntry | ICombo
    ): ICartEntry[] =>
      entries.map((entry: ICartEntry) => {
        const hasPrice = entry.price !== undefined;
        const isItem = entry.type === CartEntryType.item;
        const isCombo = entry.type === CartEntryType.combo;
        const isSwap = isCartEntryInSwaps(entry);
        const isComboSlot = parentEntry?.type === CartEntryType.comboSlot;

        if (isItem) {
          // reprices an item and its grandchild itemOptionModifiers in one go.
          const priceFunc = isComboSlot
            ? priceForItemInComboSlotSelection({
                combo: mainCombo,
                comboSlot: parentEntry,
                selectedItem: entry,
              })
            : pricingFunction({ item: entry, quantity: entry.quantity });
          return {
            ...entry,
            // prices are not defined on main items in combos, we need to preserve this
            ...(!isSwap && hasPrice && { price: priceFunc }),
            children: (entry.children || []).map(
              (itemOption: any): ICartEntry => ({
                ...itemOption,
                children: (itemOption.children || []).map((modifier: any) => ({
                  ...modifier,
                  price: priceForItemOptionModifier({ item: entry, itemOption, modifier }),
                })),
              })
            ),
          };
        }
        return {
          ...entry,
          ...(!isSwap &&
            isCombo &&
            hasPrice && { price: pricingFunction({ item: entry, quantity: entry.quantity }) }),
          children: repriceCartEntriesHelper(entry.children, entry, isCombo ? entry : mainCombo),
        };
      }),
    [
      isCartEntryInSwaps,
      pricingFunction,
      priceForItemInComboSlotSelection,
      priceForItemOptionModifier,
    ]
  );

  const repriceCartEntries = useCallback(
    (entries: ICartEntry[], parentEntry?: ICartEntry, mainCombo?: ICombo) => {
      const newEntries = repriceCartEntriesHelper(entries, parentEntry, mainCombo);
      // If newEntries is structurally equal to entries, then return original object
      // to avoid unnecessary re-renders. This is a fix to priceOrder mutation being called twice.
      return isEqual(entries, newEntries) ? entries : newEntries;
    },
    [repriceCartEntriesHelper]
  );

  return {
    addItemToCart,
    cartEntries,
    isCartEntryInSwaps,
    removeAllFromCart,
    repriceCartEntries,
    removeFromCart,
    updateQuantity,
  };
};
